import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckoutForm } from '@/components/checkout/CheckoutForm';
import { OrderSummary } from '@/components/checkout/OrderSummary';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Alert, AlertDescription } from '@/components/ui/alert';
import useCartStore from '@/store/cartStore';
import { Home, ShoppingCart, ChevronRight, Lock, Shield } from 'lucide-react';

export function CheckoutPage() {
  const navigate = useNavigate();
  const isEmpty = useCartStore((state) => state.isEmpty);
  const isCartEmpty = isEmpty();

  // 如果購物車是空的，導回產品頁（只執行一次）
  useEffect(() => {
    if (isCartEmpty) {
      navigate('/product', { replace: true });
    }
  }, [isCartEmpty, navigate]);

  // 滾動到頂部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCheckoutSuccess = (order) => {
    navigate(`/order/${order.order_id}`, { state: { order } });
  };

  // ✅ 如果購物車是空的，返回 null（不渲染任何內容）
  // useEffect 會處理導航
  if (isCartEmpty) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-screen-xl">
        {/* 麵包屑導航 */}
        <Breadcrumb className="mb-8">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/')}
                className="flex items-center gap-1 cursor-pointer hover:text-blue-600"
              >
                <Home className="h-4 w-4" />
                首頁
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/product')}
                className="cursor-pointer hover:text-blue-600"
              >
                產品
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>結帳</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* 頁面標題 */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <ShoppingCart className="h-8 w-8" />
            結帳
          </h1>
          <p className="text-gray-600">請填寫收件資訊並選擇付款方式</p>
        </div>

        {/* 安全提示 */}
        <Alert className="mb-8 border-green-200 bg-green-50">
          <Shield className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              <span>我們採用業界標準的加密技術保護您的個人資料</span>
            </div>
          </AlertDescription>
        </Alert>

        {/* 主要內容區 */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* 左側：結帳表單（佔 2 欄） */}
          <div className="lg:col-span-2">
            <CheckoutForm onSuccess={handleCheckoutSuccess} />
          </div>

          {/* 右側：訂單摘要（佔 1 欄） */}
          <div className="lg:col-span-1">
            <OrderSummary />
          </div>
        </div>

        {/* 安全說明 */}
        <div className="mt-12 bg-white rounded-lg p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            安全購物保障
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div>
              <div className="font-medium text-gray-900 mb-1">🔒 SSL 加密傳輸</div>
              <p>所有資料皆經過加密處理</p>
            </div>
            <div>
              <div className="font-medium text-gray-900 mb-1">🛡️ 隱私保護</div>
              <p>絕不外洩您的個人資料</p>
            </div>
            <div>
              <div className="font-medium text-gray-900 mb-1">✓ 7天鑑賞期</div>
              <p>收到商品後享有 7 天退換貨權益</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
